import { createAction, createSelector } from '@reduxjs/toolkit'
import isEqual from 'lodash/isEqual'
import isNull from 'lodash/isNull'
import { all, call, CallEffect, fork, ForkEffect, put, select, takeLatest } from 'redux-saga/effects'

import { sortDrawableLocationsToHaveAreasBelow } from '../utils/sortDrawableLocationsToHaveAreasBelow'
import drawShapeByType from './drawShapeByType'
import drawToolByType from './drawToolByType'
import { drawAISuggestions } from './handleDrawAISuggestions'
import { handleHighlightActiveFlag } from './handleHighlightActiveFlag'
import { handleToggleDrawableGroups2D } from './handleToggleDrawableGroups'
import {
    handleToggleMaterialFlagsVisibility,
    toggleMaterialFlagsVisibilityAction,
} from './handleToggleMaterialFlagsVisibility'
import {
    handleToggleMeasurementLinesVisibility,
    toggleMeasurementLinesVisibilityAction,
} from './handleToggleMeasurementVisibility'
import {
    handleToggleRegionLinesVisibility,
    toggleRegionLinesVisibilityAction,
} from './handleToggleRegionLinesVisibility'
import { ActiveFloor } from '../../../models/activeFloor'
import { AzureFeatureFlag } from '../../../models/azureFeatureFlags'
import { DocumentChunk } from '../../../models/documentChunk'
import { DocumentMapping } from '../../../models/documentMapping'
import { FlagFilters, NormalizedFlag, OpeningLinkWithFlagId } from '../../../models/flags'
import { Project } from '../../../models/project'
import { Building } from '../../../models/projectBuilding'
import { Region } from '../../../models/region'
import { IToolObject } from '../../../models/tool'
import { AzureFeatureFlagIds } from '../../../shared/constants/azure-feature-flags'
import { REGION_COLOR } from '../../../shared/constants/colors'
import { DRAWABLE_TYPES } from '../../../shared/constants/drawable-types'
import { isAzureFeatureFlagEnabled } from '../../../shared/services/azure-feature-flag-services/azure-feature-flag-services'
import { isFlagVisibleBasedOnFilters } from '../../../shared/services/flags/flags-service'
import { RootState } from '../../../stores'
import { convertScaleFactorLabelEnumToDecimal } from '../../../utils/calculations/scaleConversion/scaleConversion'
import { isNonAreaJoistLine } from '../../../utils/project/project-helper-functions'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import {
    Color,
    Comment,
    Cutout,
    Flag,
    Highlight,
    Image,
    Label,
    Overlay,
    PathTool,
    RegionTool,
    Select,
    Workspace,
} from '../../lib/toolBoxes/2D'
import addSelectFunctionalityToDrawable from '../../lib/utils/functionality-bindings/addSelectFunctionalityToDrawable'
import addTooltipsToFlag from '../../lib/utils/functionality-bindings/addTooltipsToFlag'
import addTooltipsToPath from '../../lib/utils/functionality-bindings/addTooltipsToPath'
import { createMultiSelectPredicates } from '../../lib/utils/MultiSelect'
import {
    DEFAULT_OVERLAY_COLOR,
    getCurrentDrawableLocations,
    selectFocusedOverlayChunkId,
    selectOpacityDict,
    selectOverlayColorDict,
    updateScaleFactor,
} from '../../slices/2D'
import { selectActiveBuildingFromActiveFloor, selectActiveFloorBuildingId } from '../../slices/buildings'
import {
    DocumentsState,
    NormalizedDocumentChunk,
    selectDocumentChunks,
    selectDocumentMappings,
    selectDrawableActiveFloor,
} from '../../slices/documents'
import { selectAzureFeatureFlags } from '../../slices/flags'
import { GeometryGroup, selectDrawableGroupsGeometries } from '../../slices/geometry'
import { sceneRendered, sceneRendering } from '../../slices/loading'
import {
    MaterialFlagsState,
    selectFlagFilters,
    selectMaterialActiveFlag,
    toggleFlagForm,
} from '../../slices/materialFlags'
import { selectProject } from '../../slices/project'
import { RegionState } from '../../slices/region'
import {
    selectAnnotationToolsState,
    selectVisibility,
    setCalibrationRatio,
    setHighlightsVisibility,
    ToolsState,
} from '../../slices/tools'
import { IMUP2DDrawableLocation, REGION_ENUMS, Vector2D } from '../../types'

// Action for triggering document markup
export const draw2DMarkup = createAction('draw2DMarkup')

interface DocumentMarkupStoreState {
    activeFloor: ActiveFloor | null
    drawableLocations: IMUP2DDrawableLocation[]
    openingGroups: GeometryGroup[]
    areaOpacity: ToolsState['areaOpacityValue']
    lineOpacity: ToolsState['lineOpacityValue']
    regions: RegionState['regions']
    documentChunks: DocumentsState['documentChunks']
    flags: MaterialFlagsState['flags']
    toolObjects: ToolsState['toolObjects']
}

export function* drawAndLabelPaths(
    documentChunkId: number | null,
    opacities: { lineOpacity: number; areaOpacity: number },
    drawableLocations: IMUP2DDrawableLocation[],
    regionPaths: paper.Path[],
    workspaceTool: Workspace,
    regionTool: RegionTool
) {
    const { lineOpacity, areaOpacity } = opacities
    /**
     * filter drawables belonging to active floor plan,
     * sort by drawing sequence order: area -> section -> point,
     * and create paths
     */
    const paths: paper.Path[] = yield all(
        drawableLocations
            .filter(
                ({ document_chunk_id, drawing_type }) =>
                    document_chunk_id === documentChunkId &&
                    drawing_type !== DRAWABLE_TYPES.NOTE &&
                    drawing_type !== DRAWABLE_TYPES.HIGHLIGHTER
            )
            // sorting locations that AREAS will be drawn first
            .sort(sortDrawableLocationsToHaveAreasBelow)
            .map((location) => call(drawShapeByType, location, areaOpacity, lineOpacity, regionPaths))
    )

    // attach tooltips to paths
    yield all(paths.map((path) => call(addTooltipsToPath, path, workspaceTool)))

    // Associate drawables with regions if there are regions on this page
    if (documentChunkId) {
        const mappings: DocumentMapping[] = yield select(selectDocumentMappings)
        const currentDocumentMapping = mappings?.find((m) => m.document_chunk_id === documentChunkId)

        for (const drawablePath of paths) {
            const item = workspaceTool.getItemWithPaperId(drawablePath.id) as paper.Path

            if (item) {
                item.data.scale = currentDocumentMapping?.scale_factor ?? ''

                for (const path of regionPaths) {
                    if (path && regionTool.isItemInsideRegion(item, path)) {
                        item.data.scale = path.data.scale
                    }
                }
            }
        }

        // Set the scale based on the document mapping
        yield put(
            updateScaleFactor({
                scale_factor: convertScaleFactorLabelEnumToDecimal(currentDocumentMapping?.scale_factor ?? ''),
            })
        )
    }
}

export const selectMarkupState = createSelector(
    (state: RootState): DocumentMarkupStoreState => ({
        activeFloor: selectDrawableActiveFloor(state),
        drawableLocations: getCurrentDrawableLocations(state.IMUP),
        openingGroups: selectDrawableGroupsGeometries(state),
        areaOpacity: state.IMUP.tools.areaOpacityValue,
        lineOpacity: state.IMUP.tools.lineOpacityValue,
        regions: state.IMUP.region.regions,
        documentChunks: state.IMUP.documents.documentChunks,
        flags: state.IMUP.materialFlags.flags,
        toolObjects: state.IMUP.tools.toolObjects,
    }),
    (state: DocumentMarkupStoreState): DocumentMarkupStoreState => state
)

type DocumentMarkupTools = [
    Color,
    Comment,
    Highlight,
    Image,
    PathTool,
    Workspace,
    RegionTool,
    Flag,
    Label,
    Select,
    Cutout,
    Overlay
]

export function* createHighlightFromLocation(
    highlightTool: Highlight,
    colorTool: Color,
    location: IMUP2DDrawableLocation
) {
    const path: paper.Path = yield call(
        highlightTool.createHighlight,
        location.coordinates,
        colorTool.createColor(`#${location.settings.color}`)
    )

    path.data.drawable_id = location.drawable_id
    path.data.opening_group_id = location.opening_group_id
    path.data.opening_location_id = location.opening_location_id
    path.data.document_chunk_id = location.document_chunk_id

    yield call(highlightTool.roundPath, path, 16)
}

export function* drawPageFlags({
    flagTool,
    workspaceTool,
    flags,
    documentId,
    shouldResetFlagCache = true,
}: {
    flagTool: Flag
    workspaceTool: Workspace
    flags: NormalizedFlag[]
    documentId: number
    // False when multiple pages/overlays are drawn in one pass -- resetting would drop the
    // flags already cached for the earlier ones.
    shouldResetFlagCache?: boolean
}) {
    const pageFlags = flags.reduce((pageFlags, flag) => {
        flag.opening_links.forEach((link) => {
            if (link.document_chunk_id === documentId) {
                pageFlags.push({
                    ...link,
                    flag_id: flag.id,
                    order: flag.order,
                    type: flag.type?.name || null,
                    status: flag.status?.id || null,
                    jiraStatusId: flag?.creationJiraStatus?.id || null,
                    categoryId: flag.category?.id || null,
                    createdByEmail: flag.created_by_user_email || null,
                })
            }
        })

        return pageFlags
    }, [] as OpeningLinkWithFlagId[])

    if (shouldResetFlagCache) {
        // reset cached flags on page change
        yield call(flagTool.resetCachedFlags)
    }

    // set flag form to closed
    yield put(toggleFlagForm({ toggleForm: false }))

    if (!!pageFlags) {
        const flagFilters: FlagFilters = yield select(selectFlagFilters)

        const flags = yield all(
            pageFlags.map((data) =>
                call(
                    flagTool.drawFlagByCoords,
                    data.coordinates,
                    data.flag_id,
                    data.opening_id,
                    data.order,
                    data.type,
                    isFlagVisibleBasedOnFilters(
                        flagFilters,
                        data.type,
                        data.status,
                        data.jiraStatusId,
                        data.categoryId,
                        data.createdByEmail
                    ),
                    data.status,
                    data.jiraStatusId,
                    false,
                    data.id,
                    data.categoryId,
                    data.createdByEmail
                )
            )
        )

        yield all(flags.map((flag) => call(addTooltipsToFlag, flag, workspaceTool)))
    }

    // if an active flag is present, highlight it
    const activeFlag: NormalizedFlag | null = yield select(selectMaterialActiveFlag)

    if (activeFlag) {
        yield call(handleHighlightActiveFlag)
    }
}

export function* sharedMarkupOperations(
    tools: {
        workspaceTool: Workspace
        imageTool: Image
        flagTool: Flag
        colorTool: Color
        pathTool: PathTool
        labelTool: Label
        regionTool: RegionTool
        highlightTool: Highlight
        commentTool: Comment
    },
    basePlan: DocumentChunk | NormalizedDocumentChunk,
    regions: Region[],
    flags: MaterialFlagsState['flags'],
    toolObjects: IToolObject[],
    drawableLocations: IMUP2DDrawableLocation[],
    openingGroups: GeometryGroup[],
    lineOpacity: number,
    areaOpacity: number
) {
    try {
        const {
            workspaceTool,
            imageTool,
            flagTool,
            colorTool,
            pathTool,
            labelTool,
            regionTool,
            highlightTool,
            commentTool,
        } = tools

        // clear the workspace for a clean canvas
        yield call(workspaceTool.clear)

        // load the plan blueprint image for the active floor
        yield call(imageTool.insertImage, basePlan.src)

        // Set the calibration ratio from the document chunk
        const xFactor = basePlan.calibration_factor_x
        const yFactor = basePlan.calibration_factor_y

        if (xFactor === yFactor) {
            yield put(setCalibrationRatio(xFactor))
        } else if (xFactor === 1 || yFactor === 1) {
            const calibrationFactor = xFactor === 1 ? yFactor : xFactor

            yield put(setCalibrationRatio(calibrationFactor))
        } else {
            const calibrationFactor = (xFactor + yFactor) / xFactor

            yield put(setCalibrationRatio(calibrationFactor))
        }

        const calibrationApplied =
            Boolean(basePlan.calibration_factor_x && basePlan.calibration_factor_x !== 1) ||
            Boolean(basePlan.calibration_factor_y && basePlan.calibration_factor_y !== 1)

        if (regions.length) {
            const localRegions: Region[] = yield regions.filter((region) => region.document_chunk_id === basePlan.id)

            if (localRegions.length) {
                const color = colorTool.createColor(REGION_COLOR)

                yield all(
                    localRegions.map((region) =>
                        call(
                            regionTool.renderRegion,
                            color,
                            region.coordinates,
                            region.id,
                            region.scale,
                            calibrationApplied
                        )
                    )
                )
            }
        }

        const regionPaths = workspaceTool.getItemsWithCriteria(
            'data',
            (data) => data.shapeType === REGION_ENUMS.TYPE
        ) as paper.Path[]

        const highlightLayers: IMUP2DDrawableLocation[] = yield drawableLocations.filter(
            ({ drawing_type, document_chunk_id }) =>
                drawing_type === DRAWABLE_TYPES.HIGHLIGHTER && document_chunk_id === basePlan.id
        )

        yield all(
            highlightLayers.reduce((highlightEffects, location) => {
                return !location.coordinates || !location.settings.color
                    ? highlightEffects
                    : [...highlightEffects, call(createHighlightFromLocation, highlightTool, colorTool, location)]
            }, [] as CallEffect[])
        )
        yield call(
            drawAndLabelPaths,
            basePlan.id ?? null,
            { lineOpacity, areaOpacity },
            drawableLocations,
            regionPaths,
            workspaceTool,
            regionTool
        )

        // determine ids of hidden drawables groups
        const hiddenDrawableGroupIds: Set<number> = openingGroups.reduce((groupIds, { id, isActive }) => {
            if (Object.values(isActive).includes(false)) {
                groupIds.add(id)
            }

            return groupIds
        }, new Set<number>())

        // build visibility predicate with hidden drawable group ids
        const visibilityTest = (item: paper.Item): boolean =>
            !(
                hiddenDrawableGroupIds.has(item.data.opening_group_id) ||
                isNonAreaJoistLine(item) ||
                item.data.document_chunk_id !== basePlan.id
            )

        // hide drawable groups per visibility test predicate
        yield call(workspaceTool.toggleDrawablesWithCondition, visibilityTest)

        // Hide all drawables that are inactive by default
        yield fork(handleToggleDrawableGroups2D)

        // get comments ("NOTE" type drawables)
        const comments: IMUP2DDrawableLocation[] = yield drawableLocations.filter(
            ({ drawing_type, document_chunk_id }) =>
                isEqual(drawing_type, DRAWABLE_TYPES.NOTE) && isEqual(document_chunk_id, basePlan.id)
        )

        // create comment pins
        yield all(
            comments.map(({ coordinates: [coordinate], settings: { text } }) =>
                call(commentTool.createComment, coordinate, text)
            )
        )

        // handle AI drawables suggestions
        yield put(drawAISuggestions())

        yield call(drawPageFlags, { flagTool, workspaceTool, flags, documentId: basePlan.id })

        const pageToolObjects = toolObjects.filter((toolObject) => toolObject.document_chunk_id === basePlan.id)

        if (!!pageToolObjects.length) {
            yield all(
                pageToolObjects.map((toolObj) =>
                    call(drawToolByType, toolObj, colorTool, pathTool, workspaceTool, lineOpacity, labelTool)
                )
            )
        }
    } catch (error) {
        console.error(error)
    }
}

export function* markupDocument() {
    try {
        // get the 2D drawing manager
        const manager: PaperManager | null = yield call(managers.get2DManager)

        // do nothing if the manager is null
        if (isNull(manager)) return

        // scene is rendering
        yield put(sceneRendering())

        // drawing needs the active building, which is fetched separately from the floor plan (e.g. after
        // a page reload). Keep the scene loading until it is available; useDocumentMarkup re-runs then.
        const floorBuildingId: number | null | undefined = yield select(selectActiveFloorBuildingId)
        const activeBuilding: Building | undefined = yield select(selectActiveBuildingFromActiveFloor)

        if (floorBuildingId && !activeBuilding) {
            yield call(
                console.error,
                'Active building is not loaded yet, waiting for project buildings to draw the scene'
            )

            return
        }

        // get markup state from redux store
        const {
            openingGroups,
            areaOpacity,
            lineOpacity,
            drawableLocations,
            activeFloor,
            regions,
            flags,
            toolObjects,
        }: DocumentMarkupStoreState = yield select(selectMarkupState)

        // get tools from manager
        const [
            colorTool,
            commentTool,
            highlightTool,
            imageTool,
            pathTool,
            workspaceTool,
            regionTool,
            flagTool,
            labelTool,
            selectTool,
            cutoutTool,
            overlayTool,
        ]: DocumentMarkupTools = yield call(manager.getTools, [
            Color.NAME,
            Comment.NAME,
            Highlight.NAME,
            Image.NAME,
            PathTool.NAME,
            Workspace.NAME,
            RegionTool.NAME,
            Flag.NAME,
            Label.NAME,
            Select.NAME,
            Cutout.NAME,
            Overlay.NAME,
        ])

        if (isNull(activeFloor)) return

        const toolsToBorrow = {
            workspaceTool,
            imageTool,
            flagTool,
            colorTool,
            pathTool,
            labelTool,
            regionTool,
            highlightTool,
            commentTool,
        }

        yield call(
            sharedMarkupOperations,
            toolsToBorrow,
            activeFloor.document_chunk,
            regions,
            flags,
            toolObjects,
            drawableLocations,
            openingGroups,
            lineOpacity,
            areaOpacity
        )

        const project: Project | null = yield select(selectProject)
        const overlaidChunkId: number | null = yield select(selectFocusedOverlayChunkId)
        const documentChunks: DocumentChunk[] | null = yield select(selectDocumentChunks)
        const { activeTool } = yield select(selectAnnotationToolsState)
        const azureFeatureFlags: AzureFeatureFlag[] | null = yield select(selectAzureFeatureFlags)
        const isQRMCommentsEnabled: boolean = isAzureFeatureFlagEnabled(
            azureFeatureFlags,
            AzureFeatureFlagIds.qrm_comments_attachments_mvp
        )

        // activate the select tool for the user unless the active tool is set to Flag
        if ((activeTool !== Flag.NAME && isQRMCommentsEnabled) || !isQRMCommentsEnabled) {
            yield call(manager.useTool, Select.NAME)
        }

        // get the visibility for region lines, material flags, measurements, and highlights
        const { isRegionLinesVisible, isMaterialFlagsVisible, isMeasurementsVisible, isHighlightsVisible } =
            yield select(selectVisibility)

        // set visibility for region lines
        yield call(handleToggleRegionLinesVisibility, {
            payload: isRegionLinesVisible,
            type: toggleRegionLinesVisibilityAction.type,
        })

        // set visibility for material flags
        yield call(handleToggleMaterialFlagsVisibility, {
            payload: isMaterialFlagsVisible,
            type: toggleMaterialFlagsVisibilityAction.type,
        })

        // set visibility for measurement lines
        yield call(handleToggleMeasurementLinesVisibility, {
            payload: isMeasurementsVisible,
            type: toggleMeasurementLinesVisibilityAction.type,
        })

        yield put(setHighlightsVisibility(isHighlightsVisible))

        yield call(selectTool.setMultiSelectionMode.bind(selectTool), createMultiSelectPredicates())

        yield call([selectTool, 'resetAreaHitTestItems'])

        yield call([cutoutTool, 'setItemMetadataUpdateFunction'], addSelectFunctionalityToDrawable)

        if (project && !project.is_master_set && overlaidChunkId && documentChunks) {
            const overlaidChunk = documentChunks.find((chunk) => chunk.id === overlaidChunkId)
            const overlayColorDict: Record<number, string> = yield select(selectOverlayColorDict)
            const overlayColor: string = overlayColorDict[overlaidChunkId] ?? DEFAULT_OVERLAY_COLOR
            const chunkOpacityDict: Record<number, number> = yield select(selectOpacityDict)

            const chunkOpacity: number = isFinite(chunkOpacityDict[overlaidChunkId])
                ? chunkOpacityDict[overlaidChunkId] / 100
                : Overlay.DEFAULT_OPACITY

            if (overlaidChunk) {
                const overlayData: {
                    scale: number
                    rotation: number
                    position: Vector2D
                } | null = yield call([overlayTool, 'extractOverlayDataToSave'], overlaidChunkId)

                yield call(overlayTool.resetOverlayRasters)

                if (!overlayData) {
                    yield call(
                        overlayTool.insertOverlay,
                        overlaidChunk.src,
                        overlaidChunkId,
                        chunkOpacity,
                        overlayColor
                    )
                } else {
                    yield call(
                        overlayTool.insertOverlay,
                        overlaidChunk.src,
                        overlaidChunkId,
                        chunkOpacity,
                        overlayColor
                    )
                    yield call([overlayTool, 'rotateImageRaster'], overlaidChunkId, overlayData.rotation)
                    yield call([overlayTool, 'updateOverlayScale'], overlaidChunkId, overlayData.scale)
                    yield call([overlayTool, 'setOverlayPositionCoordinates'], overlaidChunkId, overlayData.position)
                }
            }
        }

        // the scene is ready
        yield put(sceneRendered())
    } catch (error) {
        console.error(error)
        yield call(console.error, error as any)
    }
}

export function* watchForDocumentMarkup(): Generator<ForkEffect<never>, void, unknown> {
    yield takeLatest(draw2DMarkup.type, markupDocument)
}
